import type { Playbook } from "@workspace/api-client-react";
import { cleanSocialSourceTitle } from "@/lib/decode-html-entities";

/** Clean and decode playbook titles (esp. social OG titles with HTML entities). */
export function formatPlaybookDisplayTitle(raw: string): string {
  return cleanSocialSourceTitle(raw);
}

/** Format playbook duration for cards (actual video/read time in minutes). */
export function formatPlaybookDuration(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 1) return "< 1 min";
  return `${minutes} min`;
}

/** Duration with a verb matching the content type: "12 min watch", "5 min read", "25 min listen". */
export function formatPlaybookDurationLabel(
  playbook: Pick<Playbook, "estimatedMinutes" | "sourceType">,
): string {
  const base = formatPlaybookDuration(playbook.estimatedMinutes);
  const st = playbook.sourceType;
  if (!st || st === "youtube" || st === "ted" || st === "instagram" || st === "tiktok") {
    return `${base} watch`;
  }
  if (st === "spotify" || st === "apple_podcast") {
    return `${base} listen`;
  }
  if (st === "medium" || st === "substack" || st === "article" || st === "twitter" || st === "threads") {
    return `${base} read`;
  }
  return base;
}

/** Truncate long descriptions (e.g. full scraped article text) to a preview-friendly length. */
export function truncatePlaybookDescription(text: string, maxLength = 180): string {
  const trimmed = text.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength).trimEnd()}…`;
}

/** "18 unfoldings" — how many completed sessions started from this playbook. */
export function formatUnfoldCountLabel(playbook: Pick<Playbook, "unfoldCount">): string {
  const count = playbook.unfoldCount ?? 0;
  return `${count} unfolding${count === 1 ? "" : "s"}`;
}

/** Newest community additions first; curated built-ins follow. */
export function sortPlaybooksByAddedAt(playbooks: Playbook[]): Playbook[] {
  return [...playbooks].sort((a, b) => {
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return tb - ta;
  });
}
